import { type MouseEventHandler } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar as outlinedStar } from '@rtkwlf/pro-regular-svg-icons/faStar';
import { faStar as solidStar } from '@rtkwlf/pro-solid-svg-icons/faStar';
import { useQuery } from '@tanstack/react-query';

import {
  useCreateSpidrBlueprintFavorite,
  useDeleteBlueprintFavorite,
} from '@apis/track-public/mutations';
import { useGetOrganizationSpidrBlueprintsOptions } from '@apis/track-public/query-options';
import { IconButton, Tooltip } from '@packages/fenrir/components';
import type { IconButtonProps } from '@packages/fenrir/components';

import { useSecurityJourneyTranslations } from '../locales/use-security-journey-translations';

type FavoriteButtonProps = {
  blueprintID: string;
  size?: IconButtonProps['size'];
  stopPropagation?: boolean;
  className?: string;
};

export const FavoriteButton: React.FC<FavoriteButtonProps> = ({
  blueprintID,
  size = 'small',
  stopPropagation = false,
  className,
}) => {
  /**
   * init translations
   */
  const { t } = useSecurityJourneyTranslations();

  /**
   * query for specific blueprint that matches the provided blueprintID
   */
  const { data: isFavourited } = useQuery({
    ...useGetOrganizationSpidrBlueprintsOptions(),
    refetchOnMount: false,
    select: (data) => {
      return data.blueprints[blueprintID].isFavourited;
    },
  });

  /**
   * init mutation to favorite the blueprint
   */
  const { mutate: createFavorite } = useCreateSpidrBlueprintFavorite(
    blueprintID,
    t,
  );

  /**
   * init mutation to unfavorite the blueprint
   */
  const { mutate: deleteFavorite } = useDeleteBlueprintFavorite(blueprintID, t);

  /**
   * toggles the favorite on the blueprtin
   */
  const toggleFavorite: MouseEventHandler<HTMLButtonElement> = (e) => {
    if (stopPropagation) {
      e.stopPropagation();
    }

    if (isFavourited) {
      deleteFavorite();
    } else {
      createFavorite();
    }
  };

  return (
    <Tooltip
      title={
        isFavourited
          ? t('spidrLibrary.common.unfavoriteTooltip')
          : t('spidrLibrary.common.favoriteTooltip')
      }
      enterDelay={600}
    >
      <IconButton
        onClick={toggleFavorite}
        size={size}
        aria-pressed={isFavourited}
        data-testid={`favorite-button-${blueprintID}`}
        className={className}
      >
        <FontAwesomeIcon
          icon={isFavourited ? solidStar : outlinedStar}
          size="sm"
        />
      </IconButton>
    </Tooltip>
  );
};
